import * as React from 'react'

import moment from 'moment'

import {
  getNestedString,
  type OneMutationNotification,
  useOneMutationSubscription,
} from 'modules/ai/lib/useOneMutationSubscription'
import { dateToString } from 'modules/core/lib/dates'

// eslint-disable-next-line factorial/no-allowed-mobx-collections -- existing Time Off leaves context is still backed by the legacy collection.
import { Leaves } from 'modules/timeoff/store/collections/leaves'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

const leavesContext = React.createContext<Values | null>(null)

const LeavesProvider = leavesContext.Provider

const executePolicyChangeEmployeeIds = (
  payload: OneMutationNotification,
  scopedEmployeeId?: string
): string[] => {
  const mappings = payload.variables?.employeeMappings
  if (!Array.isArray(mappings)) return []

  const employeeIds = mappings.flatMap((mapping) => {
    if (typeof mapping !== 'object' || mapping === null) return []
    if (!('employeeId' in mapping)) return []

    const targetEmployeeId = mapping.employeeId
    return typeof targetEmployeeId === 'string' ? [targetEmployeeId] : []
  })

  if (!scopedEmployeeId) return employeeIds

  return employeeIds.includes(scopedEmployeeId) ? [scopedEmployeeId] : []
}

interface Values {
  currentAndFutureLeaves: Leaves
  fetchData: (employeeId: string) => Promise<unknown>
  fetchCount: number
}

export const LeavesContextProvider = ({
  children,
  employeeId,
}: React.PropsWithChildren<{ employeeId?: string }>) => {
  // FIXME Hack for
  // https://linear.app/factorial/issue/FRA-292/fix-issue-with-updated-leaves-not-being-reflected-in-calendar-current
  // Maybe Context is not the best way to keep the views on leaves in sync, see
  // https://github.com/factorialco/factorial-frontend/pull/4356#discussion_r559489527
  const fetchCount = React.useRef(0)
  const currentAndFutureLeaves = React.useRef(new Leaves()).current

  const fetchData: Values['fetchData'] = React.useCallback(
    (targetEmployeeId) => {
      fetchCount.current += 1

      return currentAndFutureLeaves.fetch({
        data: {
          employee_id: targetEmployeeId,
          terminated: true,
          from: dateToString(moment([moment().year(), 0, 1]), 'backend'),
          include_all_pending_approvals: true,
        },
      })
    },
    [currentAndFutureLeaves]
  )

  const handleCreateLeave = useUnsafeCallback(
    async (payload: OneMutationNotification) => {
      const targetEmployeeId =
        getNestedString(payload.result, [
          'timeoffMutations',
          'createTimeoffLeave',
          'leave',
          'employee',
          'id',
        ]) ?? employeeId

      if (!targetEmployeeId) {
        return
      }

      if (employeeId && targetEmployeeId !== employeeId) {
        return
      }

      await fetchData(targetEmployeeId)
    },
    [employeeId, fetchData]
  )

  const handleExecutePolicyChange = useUnsafeCallback(
    async (payload: OneMutationNotification) => {
      const targetEmployeeIds = executePolicyChangeEmployeeIds(payload, employeeId)

      if (targetEmployeeIds.length === 0) {
        return
      }

      await Promise.all(targetEmployeeIds.map((targetEmployeeId) => fetchData(targetEmployeeId)))
    },
    [employeeId, fetchData]
  )

  useOneMutationSubscription('CreateLeave', handleCreateLeave)
  useOneMutationSubscription('ExecuteTimeoffPolicyChange', handleExecutePolicyChange)

  const values: Values = {
    currentAndFutureLeaves,
    fetchData,
    fetchCount: fetchCount.current,
  }

  return <LeavesProvider value={values}>{children}</LeavesProvider>
}

export const useLeaves = () => {
  const data = React.useContext(leavesContext)

  if (!data) {
    throw new Error('useLeaves must be used within a collection provider')
  }

  return data
}
